import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { ComcastErrorType, generateErrorData } from '../../../../shared/helpers'
import googleAnalytics4 from '../../lib/googleAnalytics'
import { getShowDetails } from '../../actions/contentActions'
import Spinner from '../../components/Spinner'
import { useAppSelector } from '../../hooks/useStore'
import Platform from '../../platforms'
import type { RootState } from '../../reducers/types/reducerTypes'
import ShowDetailsContainer from './ShowDetailsContainer'

function ShowDetailsScreen() {
  const [spinnerVisible, setSpinnerVisible] = useState(true)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const showDetail = useAppSelector((state: RootState) => state.showDetail)
  const content = useAppSelector((state: RootState) => state.content)
  const userId = useAppSelector((state: RootState) => state.user.pid)

  useEffect(() => {
    Platform.reportAppReady()

    // TODO: remove this when whole app is migrated to react spatial navigation
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
      resetContent()
    }
  }, [])

  const resetContent = () => {
    dispatch({
      type: 'RESET_SHOW_DETAILS_DATA',
    })
  }

  useEffect(() => {
    if (slug && userId) {
      setSpinnerVisible(true)
      resetContent()
      buildScreen()
    }
  }, [slug, userId])

  useEffect(() => {
    const { contentUrl, contentHasSecondsWatched } = showDetail.continueWatchingData
    const secondsWatched = content.videoDetails.seconds_watched
    if (
      contentUrl &&
      !contentHasSecondsWatched &&
      secondsWatched > 0 &&
      contentUrl === content.id
    ) {
      dispatch({
        type: 'SET_SHOW_DETAILS_DATA',
        payload: {
          continueWatchingData: {
            ...showDetail.continueWatchingData,
            contentHasSecondsWatched: true,
            contentSecondsWatched: secondsWatched,
          },
        },
      })
    }
  }, [showDetail.continueWatchingData.contentUrl, content.videoDetails.seconds_watched, content.id])

  useEffect(() => {
    if (showDetail && showDetail.pageDetails && showDetail.pageDetails.cid) {
      setSpinnerVisible(false)

      googleAnalytics4.sendPageViewEvent(
        `Show Detail | ${showDetail.pageDetails.title}`
      )
    }
  }, [showDetail.pageDetails?.cid])

  const buildScreen = () => {
    try {
      getShowDetails(slug || '', userId)
    } catch (error) {
      setSpinnerVisible(false)
      const errorObj = generateErrorData(error)
      dispatch({
        type: 'TRIGGER_ERROR',
        payload: {
          error: {
            ...errorObj,
            content: { errorPath: location.pathname },
            codeBaseLocation: 'ShowDetailsScreen - getShowInfo',
            errorType: ComcastErrorType.network,
          },
        },
      })
      navigate('/error', { replace: true })
    }
  }

  return (
    <>
      {spinnerVisible && <Spinner />}
      {!spinnerVisible && <ShowDetailsContainer showDetail={showDetail} />}
    </>
  )
}

export default ShowDetailsScreen
